// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import {
  handleApiCall,
  buildParams,
  cachedGet,
  invalidateGetCache,
} from "../utils/apiHelper";

const TENANTS_CACHE_TTL = 60_000;

const invalidateTenantsCache = () => invalidateGetCache("/admin/tenants");

const tenantService = {
  /**
   * Fetch tenants with pagination and sorting.
   */
  async getTenants(params = {}) {
    const queryParams = buildParams({
      page: params.page,
      limit: params.limit,
      sort_by: params.sort_by,
      sort_order: params.sort_order,
      status: params.status,
      search: params.search,
      tenant_id: params.tenant_id,
    });

    return handleApiCall(
      () =>
        cachedGet("/admin/tenants/", {
          params: queryParams,
          ttl: TENANTS_CACHE_TTL,
        }),
      "Error fetching tenants",
    );
  },

  async getPlans(params = {}) {
    const queryParams = buildParams({
      page: params.page || 1,
      limit: params.limit || 100,
    });
    return handleApiCall(
      () => apiClient.get("/admin/tenants/plans/list", { params: queryParams }),
      "Error fetching plans",
    );
  },

  /**
   * Create a new subscription/billing plan
   */
  async createPlan(planData) {
    return handleApiCall(
      () => apiClient.post("/admin/tenants/plans", planData),
      "Error creating plan",
    );
  },

  /**
   * Update an existing plan (partial update — only supplied fields change).
   *
   * Backend route is /admin/tenants/plans/{plan_id} (no trailing slash, like
   * the POST create route). Super Admin only. Returns 404 if the plan does
   * not exist, 409 if plan_code clashes with another plan.
   */
  async updatePlan(planId, planData) {
    return handleApiCall(
      () => apiClient.put(`/admin/tenants/plans/${planId}`, planData),
      `Error updating plan ${planId}`,
    );
  },

  /**
   * Delete a plan.
   *
   * Backend route is /admin/tenants/plans/{plan_id} (no trailing slash). Super
   * Admin only. Returns 404 if the plan does not exist, 409 if the plan is
   * still referenced by a tenant/subscription (deactivate via is_active=false
   * instead).
   */
  async deletePlan(planId) {
    return handleApiCall(
      () => apiClient.delete(`/admin/tenants/plans/${planId}`),
      `Error deleting plan ${planId}`,
    );
  },

  /**
   * Get all tenants (for dropdowns and filters)
   */
  async getAllTenantsForFilter(options = {}) {
    const {
      includeAll = true,
      allLabel = "All Tenants",
      ...queryParams
    } = options;

    return handleApiCall(
      async () => {
        const response = await this.getTenants({
          limit: 25,
          ...queryParams,
        });
        const tenants =
          response.tenants || response.items || response.data || [];

        const formattedTenants = tenants.map((tenant) => ({
          id: tenant.tenant_id || tenant.id,
          name: tenant.name,
          subdomain: tenant.subdomain || "",
          status: tenant.status,
        }));

        if (includeAll) {
          return [
            { id: "all", name: allLabel, subdomain: "all" },
            ...formattedTenants,
          ];
        }

        return formattedTenants;
      },
      "Error fetching tenants for filter",
      includeAll ? [{ id: "all", name: allLabel, subdomain: "all" }] : [],
    );
  },

  /**
   * Role-aware tenant list for dropdowns (GET /admin/tenants/all). Super
   * Admins get every tenant (sorted by name); Tenant Admins get only their
   * own. Returns the raw TenantResponse list (each has tenant_id + name).
   */
  async getAllTenants() {
    return handleApiCall(
      () => apiClient.get("/admin/tenants/all"),
      "Error fetching all tenants",
    );
  },

  /**
   * Get tenant by ID
   */
  async getTenantById(tenantId) {
    return handleApiCall(
      () => apiClient.get(`/admin/tenants/${tenantId}`),
      `Error fetching tenant ${tenantId}`,
    );
  },

  async createTenant(tenantData) {
    const result = await handleApiCall(
      () => apiClient.post("/admin/tenants/", tenantData),
      "Error creating tenant",
    );
    invalidateTenantsCache();
    return result;
  },

  async updateTenant(tenantId, tenantData) {
    const result = await handleApiCall(
      () => apiClient.put(`/admin/tenants/${tenantId}`, tenantData),
      `Error updating tenant ${tenantId}`,
    );
    invalidateTenantsCache();
    return result;
  },

  async deleteTenant(tenantId) {
    const result = await handleApiCall(
      () => apiClient.delete(`/admin/tenants/${tenantId}`),
      `Error deleting tenant ${tenantId}`,
    );
    invalidateTenantsCache();
    return result;
  },

  /**
   * Get locations assigned to a tenant
   */
  async getTenantLocations(tenantId) {
    return handleApiCall(
      () => apiClient.get(`/admin/tenants/${tenantId}/locations`),
      `Error fetching locations for tenant ${tenantId}`,
    );
  },

  /**
   * Assign a location to a tenant
   */
  async assignLocationToTenant(tenantId, data) {
    return handleApiCall(
      () => apiClient.post(`/admin/tenants/${tenantId}/locations`, data),
      `Error assigning location to tenant ${tenantId}`,
    );
  },

  /**
   * Update a tenant-location assignment
   */
  async updateTenantLocation(tenantId, locationId, data) {
    return handleApiCall(
      () =>
        apiClient.put(
          `/admin/tenants/${tenantId}/locations/${locationId}`,
          data,
        ),
      `Error updating tenant location ${locationId}`,
    );
  },

  /**
   * Remove a location from a tenant
   */
  async removeLocationFromTenant(tenantId, locationId) {
    return handleApiCall(
      () =>
        apiClient.delete(`/admin/tenants/${tenantId}/locations/${locationId}`),
      `Error removing location ${locationId} from tenant ${tenantId}`,
    );
  },

  /**
   * Fetch tenant management overview stats (active tenants, bookings, users, hierarchy counts).
   */
  async getTenantManagementStats() {
    const response = await apiClient.get("/admin/tenants/stats");
    return response?.data ?? response;
  },
};

export default tenantService;
